/**
 * অবস্থা ৫ · প্রদানকারী অনুপলব্ধ — `P0 States § ৮` (Unavailable · Provider).
 * `E_PROVIDER_UNAVAILABLE`.
 *
 * Frame note: "The named next opening turns a dead end into a booking, and two
 * available alternatives sit below it. A user with a broken AC today needs both
 * options, in that order."
 *
 * W1-04 · THE FRAME IS ONE PROVIDER'S PAGE, AND THAT PROVIDER DOES NOT EXIST.
 * Everything § ৮ draws below its image is a fact about a particular provider: a name, an
 * area, "আজ বন্ধ", "আজ সব স্লট পূর্ণ", the next free slot with a booking button on it, and
 * two named alternatives with star ratings and distances. This component used to print
 * all of it, as drawn, over any `E_PROVIDER_UNAVAILABLE` — including the ratings of two
 * businesses that are not on the platform. The founder decision of 2026-09-18 is that no
 * fictitious provider, rating or verification is shown, and the error envelope carries
 * none of those facts (`packages/contracts § ApiErrorBody`), so none is drawn.
 *
 * What is left keeps the frame's panel and its one rule — a way on, not a dead end:
 *
 *   · the panel, headed by the server's `title_bn` or by the state's own drawn name
 *     (`keys.ts § STATE_LABEL_BN`), with the server's sentence under it when it sent one;
 *   · the search door, as the way on, in the panel's own teal button.
 *
 * When the server can name the next opening and real alternatives, they belong in the
 * envelope and back in this frame, in the drawn order. Recorded in
 * `docs/deviations/W1-04.md` (S8).
 */
import { ariaLabel } from '@oprappo/tokens';
import { StateFrame, BackChevron } from './chrome.js';
import { PROVIDER_UNAVAILABLE } from './data.js';
import { STATE_LABEL_BN } from './keys.js';

export interface ProviderUnavailableStateProps {
  /** `title_bn` from the refusal, when the server has a headline for it. */
  titleBn?: string | undefined;
  /** `error.message_bn`. Absent or empty: not drawn. */
  bodyBn?: string | undefined;
  onSearch?: (() => void) | undefined;
  onBack?: (() => void) | undefined;
}

function said(value: string | undefined): value is string {
  return value !== undefined && value.trim() !== '';
}

export function ProviderUnavailableState({
  titleBn,
  bodyBn,
  onSearch,
  onBack,
}: ProviderUnavailableStateProps = {}): JSX.Element {
  return (
    <StateFrame stateKey="provider_unavailable">
      <div className="bg-white border-b border-border px-[16px] py-[14px] flex gap-[12px] items-center">
        <button
          type="button"
          aria-label={ariaLabel.back}
          onClick={onBack}
          className="bg-transparent border-0 p-0 rounded-none flex-none"
        >
          <BackChevron />
        </button>
      </div>

      <div className="flex-1 p-[16px] oprappo-scroll">
        {/* The panel § ৮ puts the next opening in: amber-text rule, its own action. */}
        <div className="bg-white border border-border border-l-[3px] border-l-amber-text p-[14px]">
          <div className="font-anek font-semibold text-[16px] leading-[1.75] text-ink">
            {said(titleBn) ? titleBn : STATE_LABEL_BN.provider_unavailable}
          </div>
          {said(bodyBn) ? (
            <div className="font-anek font-normal text-[14.5px] leading-[1.75] text-ink2 mt-[6px]">{bodyBn}</div>
          ) : null}
          <button
            type="button"
            onClick={onSearch}
            className="w-full bg-teal h-[46px] p-0 flex items-center justify-center font-anek font-semibold text-[15px] leading-[1.75] text-white mt-[13px] border-0 rounded-none"
          >
            {PROVIDER_UNAVAILABLE.search_action_bn}
          </button>
        </div>
      </div>
    </StateFrame>
  );
}
